import { useState } from 'react';
import { FileText, Download, ExternalLink, Copy, Check } from 'lucide-react';

interface ReportAccessProps {
  accessToken: string;
  accessUrl: string;
}

export function ReportAccess({ accessToken, accessUrl }: ReportAccessProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(accessUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="bg-gradient-to-br from-teal-900/40 to-blue-900/40 border border-teal-500/30 rounded-lg p-8 mb-8">
      <div className="flex items-center mb-4">
        <FileText className="text-teal-400 mr-3" size={32} />
        <h2 className="text-2xl font-bold text-white">Your Career Intelligence Report</h2>
      </div>

      <p className="text-gray-300 mb-6">
        Your personalized career report is ready! Access it now or save the link for later.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <a
          href={accessUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-lg"
        >
          <ExternalLink className="mr-2" size={20} />
          View Report Now
        </a>

        <a
          href={accessUrl}
          download
          className="flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-lg"
        >
          <Download className="mr-2" size={20} />
          Download Report
        </a>
      </div>

      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-400">Direct Access Link</span>
          <button
            onClick={handleCopyLink}
            className="flex items-center text-teal-400 hover:text-teal-300 text-sm font-medium"
          >
            {copied ? (
              <>
                <Check className="mr-1" size={16} />
                Copied!
              </>
            ) : (
              <>
                <Copy className="mr-1" size={16} />
                Copy Link
              </>
            )}
          </button>
        </div>
        <code className="text-xs text-gray-300 break-all block">{accessUrl}</code>
      </div>

      <div className="mt-4 text-sm text-gray-400 italic">
        Keep this link secure. Anyone with this link can access your report.
      </div>
    </div>
  );
}
